import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Button, Skeleton, Tooltip } from '@radix-ui/themes'
import { v4 as uuidv4 } from 'uuid'
import { IconChevronDown, IconChevronRight, IconCircleMinus, IconHierarchy, IconServer } from '@tabler/icons-react'
import Scroll from '../scroll/Scroll'
import { DeviceForensic } from '../../types/DeviceForensicTypes'
import { inPov } from '../../utils/session'
import { selectUser } from '../../reducers/users'
import { useSelector } from 'react-redux'
import { formatUtcTimestamp } from '../../utils/dates'

interface TreeTableProps {
  data: DeviceForensic[]
  loading: boolean
  lastItemClassName?: string
  setShowPopout: (show: boolean) => void
  setPopoutData: (data: DeviceForensic) => void
  expanded?: string[]
  setExpanded: Dispatch<SetStateAction<Set<string>>>
}

interface TreeRowProps {
  item: DeviceForensic
  level: number
  parent?: string
  expandedPaths: Set<string>
  toggleExpand: (path: string) => void
}

const ForensicTreeTable: React.FC<TreeTableProps> = (props: TreeTableProps) => {
  const { session } = useSelector(selectUser)

  const TreeRow: React.FC<TreeRowProps> = ({ item, level, parent, expandedPaths, toggleExpand }) => {
    const isExpanded = expandedPaths.has(item.path)
    const hasChildren = item.children && item.children.length > 0

    return (
      <>
        <tr className=" border-gray-200">
          <td
            key={uuidv4()}
            className={`h-14 border-mainBorder border-b last:border-b-0 !bg-tableRowGrey pl-4 max-w-[200px] lg:max-w-[250px] xl:max-w-[400px] 2xl:max-w-[570px]`}
          >
            <div
              className={`flex items-center ${hasChildren ? 'cursor-pointer' : ``}`}
              onClick={() => hasChildren && toggleExpand(item.path)}
              style={{ paddingLeft: `${level * 20}px` }}
            >
              {hasChildren ? (
                <span className="mr-2">
                  {isExpanded ? <IconChevronDown className="h-4 w-4" /> : <IconChevronRight className="h-4 w-4" />}
                </span>
              ) : (
                <span className="h-4 w-6" />
              )}
              <Scroll classNames="my-2 pb-1">
                <div className={`flex ${item.deleted ? 'text-textTertiary' : ''}`}>
                  {item.path.replace(parent ?? '', '').replace(/^\/+/, '')}
                </div>
              </Scroll>
              {item.deleted == true && (
                <Tooltip
                  content={
                    <p>
                      Record deleted on&nbsp;
                      {item.deletedTimestamp ? formatUtcTimestamp(item.deletedTimestamp, true, true) : ''}
                    </p>
                  }
                >
                  <span className="flex justify-center">
                    <IconCircleMinus className="text-warningStrong h-5 w-5 ml-2 flex-shrink-0" />
                  </span>
                </Tooltip>
              )}
              {item.inodeVersions && item.inodeVersions.length > 1 && (
                <Tooltip content={<p>{item.inodeVersions.length} inodes</p>}>
                  <IconHierarchy className="h-4 w-4 ml-2 flex-shrink-0" />
                </Tooltip>
              )}
              {item.mounts && item.mounts.length > 0 && (
                <Tooltip content={<p>Mounted</p>}>
                  <IconServer className="h-5 w-5 ml-2 flex-shrink-0" />
                </Tooltip>
              )}
            </div>
          </td>
          <td className="h-14 border-mainBorder border-b !bg-tableRowGrey text-center">
            {item.fileEventCount !== undefined && item.fileEventCount !== null && item.fileEventCount >= 1 ? (
              item.fileEventCount
            ) : (
              <span className="cursor-default">--</span>
            )}
          </td>
          <td className="h-14 border-mainBorder border-b !bg-tableRowGrey text-center">
            {item.items !== undefined && item.items !== null && item.items >= 0 ? (
              item.items
            ) : (
              <Tooltip content="Unable to fetch item count">
                <span className="cursor-default">--</span>
              </Tooltip>
            )}
          </td>
          <td className={`h-14 border-mainBorder border-b !bg-tableRowGrey ${item.deleted ? 'text-textTertiary' : ''}`}>
            {item.firstSeen ? formatUtcTimestamp(item.firstSeen, true) : ''}
          </td>
          <td className={`h-14 border-mainBorder border-b !bg-tableRowGrey ${item.deleted ? 'text-textTertiary' : ''}`}>
            {item.lastSeen ? formatUtcTimestamp(item.lastSeen, true) : ''}
          </td>
          <td className="h-14 border-mainBorder border-b !bg-tableRowGrey pr-4">
            <Button
              size={'2'}
              className="cursor-pointer float-right"
              onClick={() => {
                setPopoutData({ ...item, shortPath: item.path.replace(parent ?? '', '').replace(/^\/+/, '') })
                setShowPopout(true)
              }}
              variant="outline"
            >
              View
            </Button>
          </td>
        </tr>
        {isExpanded &&
          [...item.children]
            .sort((a, b) => a.path.localeCompare(b.path))
            .map((child, index) => (
              <TreeRow
                key={index}
                item={child}
                level={level + 1}
                parent={item.path}
                expandedPaths={expandedPaths}
                toggleExpand={toggleExpand}
              />
            ))}
      </>
    )
  }

  const { data, loading, lastItemClassName = '', setShowPopout, setPopoutData, expanded, setExpanded } = props
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set())

  const toggleExpand = (path: string) => {
    setExpanded((prev: Set<string>) => {
      const newSet = new Set(prev)
      if (newSet.has(path)) {
        newSet.delete(path)
      } else {
        newSet.add(path)
      }
      return newSet
    })
  }

  useEffect(() => {
    if (expanded) {
      setExpandedPaths(new Set(expanded))
    }
  }, [expanded])

  return (
    <>
      {data && !loading && (
        <div
          className={`${inPov(session) ? 'max-h-[calc(100vh_-_230px)]' : 'max-h-[calc(100vh_-_195px)]'} overflow-y-auto border border-mainBorder border-collapse rounded-lg`}
        >
          <table className="w-full table-auto">
            <thead
              className="h-[56px] border-mainBorder border-b bg-tableHeaderGrey sticky top-0"
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}
            >
              <tr key={uuidv4()} className="text-left h-[56px] font-normal text-sm">
                <th className="font-normal text-sm pl-4 w-[500px] lg:w-[250px] xl:w-[400px] 2xl:w-[570px]">
                  Directory
                </th>
                <th className="font-normal text-sm w-[100px] items-center text-center">Events</th>
                <th className="font-normal text-sm w-[100px] items-center text-center">Items</th>
                <th className="font-normal text-sm w-[140px]">First Seen (UTC)</th>
                <th className="font-normal text-sm w-[140px]">Last Seen (UTC)</th>
                <th className="font-normal text-sm pl-2 w-[60px]">
                  <div className={`${lastItemClassName} items-center flex`}></div>
                </th>
              </tr>
            </thead>
            <tbody>
              {[...data]
                .sort((a, b) => a.path.localeCompare(b.path))
                .map((item, index) => (
                  <TreeRow
                    key={index}
                    item={item}
                    level={0}
                    expandedPaths={expandedPaths}
                    toggleExpand={toggleExpand}
                  />
                ))}
            </tbody>
          </table>
        </div>
      )}

      {(!data || loading) && (
        <div className="flex justify-center items-center mt-20">
          <Skeleton className="h-[300px] w-full flex-grow" />
        </div>
      )}
    </>
  )
}

export default ForensicTreeTable
